import React, { useEffect, useState } from 'react';
import { CreditCard, Loader, Check } from 'lucide-react';
import authService from '../../services/authService';
import { debugError } from '../../utils/logger';

const DOCUMENT_TYPES = [
  { value: 'CC', label: 'Cédula de ciudadanía (CC)' },
  { value: 'CE', label: 'Cédula de extranjería (CE)' },
  { value: 'TI', label: 'Tarjeta de identidad (TI)' },
  { value: 'PP', label: 'Pasaporte (PP)' }
];

// Editor del documento de identificación (cédula) en la pestaña "Perfil".
// Carga el valor actual desde /auth/me y lo actualiza vía PUT /auth/document.
function DocumentEditor() {
  const [documentType, setDocumentType] = useState('CC');
  const [documentNumber, setDocumentNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await authService.getMe();
        if (mounted) {
          setDocumentType(res?.data?.user?.documentType || 'CC');
          setDocumentNumber(res?.data?.user?.documentNumber || '');
        }
      } catch (err) {
        debugError('Error cargando documento de identificación:', err);
        if (mounted) setError('No se pudo cargar tu documento de identificación');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleNumberChange = (e) => {
    setDocumentNumber(e.target.value.replace(/\D/g, '').slice(0, 15));
  };

  const handleSave = async () => {
    if (!/^\d{6,15}$/.test(documentNumber)) {
      setError('Ingresa un número de documento válido (solo dígitos)');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await authService.updateDocumentNumber(documentType, documentNumber);
      setDocumentType(res?.data?.documentType || documentType);
      setDocumentNumber(res?.data?.documentNumber || documentNumber);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      debugError('Error actualizando documento de identificación:', err);
      setError(err?.response?.data?.message || 'No se pudo actualizar tu documento de identificación.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-xl">
      <div className="flex items-center gap-3 mb-4">
        <CreditCard className="h-6 w-6 text-primary-600" />
        <h3 className="text-lg font-bold text-gray-800">Documento de identificación</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader className="h-4 w-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">
            Tu número de documento (cédula) es requerido para identificarte en tus consultas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              disabled={saving}
              className="input-field sm:w-56"
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="numeric"
              value={documentNumber}
              onChange={handleNumberChange}
              disabled={saving}
              placeholder="1234567890"
              className="input-field flex-1"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-primary flex items-center justify-center"
            >
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </button>
          </div>

          <div className="mt-3 min-h-[20px] text-sm">
            {!saving && savedAt && (
              <span className="text-green-600 inline-flex items-center gap-1">
                <Check className="h-4 w-4" /> Documento guardado
              </span>
            )}
            {error && <span className="text-red-600">{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

export default DocumentEditor;
